import type { GroupKind } from "./groups";
import type { CatalogGroup, CatalogItem, CommissionModel, MemberCommissionSetup } from "./types";

/** A group of the model editor; `groupId` null is the "no group" bucket (item rates only). */
export type ModelGroupNode = {
  groupId: string | null;
  name: string;
  rate: number | null;
  items: Array<{ id: string; name: string; sale_price: number | null; own: number | null; effective: number | null }>;
  ownCount: number;
};

/** Reuses prices already loaded by the catalog without widening the commission RPC. */
export function attachSalePrices<T extends { id: string }>(
  items: T[], prices: Array<{ id: string; sale_price: number | null }>,
): Array<T & { sale_price: number | null }> {
  const byId = new Map(prices.map((item) => [item.id, item.sale_price]));
  return items.map((item) => ({ ...item, sale_price: byId.get(item.id) ?? null }));
}

const byName = (locale: string) => (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, locale);

/** Items of `kind` nested under their groups, with the model's group and item rates. */
export function buildModelTree(
  kind: GroupKind, groups: CatalogGroup[], items: CatalogItem[], model: CommissionModel | undefined, locale = "pt-BR",
): ModelGroupNode[] {
  const groupRate = new Map((model?.rates ?? []).filter((r) => r.kind === kind).map((r) => [r.group_id, r.percent]));
  const itemRate = new Map((model?.item_rates ?? []).map((r) => [r.item_id, r.percent]));
  const kindItems = items.filter((i) => i.kind === kind).sort(byName(locale));

  const node = (groupId: string | null, name: string): ModelGroupNode => {
    const rate = groupId ? groupRate.get(groupId) ?? null : null;
    const nodeItems = kindItems
      .filter((i) => i.group_id === groupId)
      .map((i) => {
        const own = itemRate.get(i.id) ?? null;
        return { id: i.id, name: i.name, sale_price: i.sale_price ?? null, own, effective: own ?? rate };
      });
    return { groupId, name, rate, items: nodeItems, ownCount: nodeItems.filter((i) => i.own !== null).length };
  };

  const nodes = groups
    .filter((g) => g.kind === kind)
    .sort(byName(locale))
    .map((g) => node(g.id, g.name))
    .filter((n) => n.items.length > 0 || n.rate !== null);
  const ungrouped = node(null, "");
  return ungrouped.items.length > 0 ? [...nodes, ungrouped] : nodes;
}

/** Commission value in currency units; null means this item has no configured rate. */
export function commissionAmount(salePrice: number | null, percent: number | null): number | null {
  return salePrice === null || percent === null ? null : salePrice * percent / 100;
}

/** A group is mixed when at least one item has its own commission rate. */
export function isGroupRateMixed(rate: number | null, items: Array<{ own: number | null }>): boolean {
  return items.some((item) => item.own !== null && item.own !== rate);
}

/** A group on the member page: what the model pays and the person's own exception. */
export type MemberGroupNode = {
  groupId: string | null;
  name: string;
  modelRate: number | null;
  override: number | null;
  items: Array<{ id: string; name: string; modelRate: number | null; override: number | null; effective: number | null }>;
  exceptionCount: number;
};

type Lists = { groups: Array<{ id: string; name: string }>; items: Array<{ id: string; name: string; group_id: string | null }> };

/**
 * Resolves the person's % per item, in the same order the sale uses:
 * person item → person group → model item → model group.
 * Pass the catalog lists to show every item; without them (read-only view) the tree only has
 * the groups and items the setup mentions.
 */
export function buildMemberTree(kind: GroupKind, setup: MemberCommissionSetup, catalog: Lists | null, locale = "pt-BR"): MemberGroupNode[] {
  const ofKind = <T extends { kind: GroupKind }>(xs: T[]) => xs.filter((x) => x.kind === kind);
  const modelGroup = new Map(ofKind(setup.model_rates).map((r) => [r.group_id, r.percent]));
  const personGroup = new Map(ofKind(setup.overrides).map((r) => [r.group_id, r.percent]));
  const modelItem = new Map(ofKind(setup.model_item_rates).map((r) => [r.item_id, r.percent]));
  const personItem = new Map(ofKind(setup.item_overrides).map((r) => [r.item_id, r.percent]));

  const lists: Lists = catalog ?? (() => {
    const groups = new Map<string, string>();
    for (const r of [...ofKind(setup.model_rates), ...ofKind(setup.overrides)]) groups.set(r.group_id, r.group_name);
    const items = new Map<string, { id: string; name: string; group_id: string | null }>();
    for (const r of [...ofKind(setup.model_item_rates), ...ofKind(setup.item_overrides)]) {
      items.set(r.item_id, { id: r.item_id, name: r.item_name, group_id: r.group_id });
      if (r.group_id && r.group_name) groups.set(r.group_id, r.group_name);
    }
    return { groups: [...groups].map(([id, name]) => ({ id, name })), items: [...items.values()] };
  })();
  const sortedItems = [...lists.items].sort(byName(locale));

  const node = (groupId: string | null, name: string): MemberGroupNode => {
    const gModel = groupId ? modelGroup.get(groupId) ?? null : null;
    const gPerson = groupId ? personGroup.get(groupId) ?? null : null;
    const items = sortedItems
      .filter((i) => i.group_id === groupId)
      .map((i) => {
        const inModel = modelItem.get(i.id) ?? gModel;
        const override = personItem.get(i.id) ?? null;
        return { id: i.id, name: i.name, modelRate: inModel, override, effective: override ?? gPerson ?? modelItem.get(i.id) ?? gModel };
      });
    return {
      groupId, name, modelRate: gModel, override: gPerson, items,
      exceptionCount: items.filter((i) => i.override !== null).length + (gPerson !== null ? 1 : 0),
    };
  };

  const nodes = [...lists.groups]
    .sort(byName(locale))
    .map((g) => node(g.id, g.name))
    .filter((n) => n.items.length > 0 || n.modelRate !== null || n.override !== null);
  const ungrouped = node(null, "");
  return ungrouped.items.length > 0 ? [...nodes, ungrouped] : nodes;
}

/** Case- and accent-insensitive search over group and item names; matching groups keep all items. */
export function filterTree<T extends { name: string; items: Array<{ name: string }> }>(nodes: T[], query: string): T[] {
  const norm = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
  const q = norm(query.trim());
  if (!q) return nodes;
  return nodes
    .map((n) => (norm(n.name).includes(q) ? n : { ...n, items: n.items.filter((i) => norm(i.name).includes(q)) }))
    .filter((n) => n.items.length > 0);
}

/** Parses a pt-BR percent typed in PercentInput ("12,5" / "1.012,5"); "" → null. */
export function parsePercent(raw: string): number | null {
  const clean = raw.replace(/\./g, "").replace(",", ".").trim();
  return clean === "" ? null : Number(clean);
}
